/**
 * Risk Assessment PDF Document
 *
 * Generates a professional PDF report for container risk assessment.
 */

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    pdf,
} from '@react-pdf/renderer';

// Brand colors matching the platform theme
const colors = {
    brand: {
        primary: '#7F56D9',
        secondary: '#9E77ED',
    },
    gray: {
        25: '#FCFCFD',
        50: '#F9FAFB',
        100: '#F2F4F7',
        200: '#EAECF0',
        300: '#D0D5DD',
        400: '#98A2B3',
        500: '#667085',
        600: '#475467',
        700: '#344054',
        800: '#1D2939',
        900: '#101828',
    },
    success: {
        50: '#ECFDF3',
        500: '#12B76A',
        600: '#039855',
        700: '#027A48',
    },
    warning: {
        50: '#FFFAEB',
        500: '#F79009',
        600: '#DC6803',
        700: '#B54708',
    },
    error: {
        50: '#FEF3F2',
        500: '#F04438',
        600: '#D92D20',
        700: '#B42318',
    },
};

// Styles for the PDF document
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontFamily: 'Helvetica',
        backgroundColor: '#FFFFFF',
    },
    header: {
        marginBottom: 30,
        borderBottomWidth: 1,
        borderBottomColor: colors.gray[200],
        paddingBottom: 20,
    },
    logo: {
        fontSize: 24,
        fontWeight: 'bold',
        color: colors.brand.primary,
        marginBottom: 4,
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: colors.gray[900],
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 11,
        color: colors.gray[500],
    },
    dateText: {
        fontSize: 10,
        color: colors.gray[400],
        marginTop: 8,
    },
    section: {
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: colors.gray[800],
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    overallScoreCard: {
        padding: 16,
        borderRadius: 8,
        marginBottom: 20,
    },
    overallScoreRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    overallScoreLeft: {
        flexDirection: 'column',
    },
    overallScoreTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: colors.gray[900],
        marginBottom: 4,
    },
    overallScoreSubtitle: {
        fontSize: 10,
        color: colors.gray[600],
    },
    overallScoreValue: {
        fontSize: 32,
        fontWeight: 'bold',
        color: colors.gray[900],
    },
    badge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        marginTop: 6,
    },
    badgeText: {
        fontSize: 10,
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    componentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.gray[100],
    },
    componentLabel: {
        flex: 1,
        fontSize: 11,
        color: colors.gray[700],
    },
    componentScore: {
        fontSize: 11,
        fontWeight: 'bold',
        color: colors.gray[900],
        marginRight: 12,
        width: 40,
        textAlign: 'right',
    },
    progressBarContainer: {
        width: 120,
        height: 6,
        backgroundColor: colors.gray[200],
        borderRadius: 3,
    },
    progressBar: {
        height: 6,
        borderRadius: 3,
    },
    factorCard: {
        backgroundColor: colors.gray[50],
        padding: 12,
        borderRadius: 6,
        marginBottom: 8,
        borderLeftWidth: 3,
        borderLeftColor: colors.warning[500],
    },
    factorHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 4,
    },
    factorTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: colors.gray[800],
        flex: 1,
    },
    factorContribution: {
        fontSize: 10,
        fontWeight: 'bold',
        color: colors.warning[600],
        backgroundColor: colors.warning[50],
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    factorDescription: {
        fontSize: 10,
        color: colors.gray[600],
        lineHeight: 1.4,
    },
    recommendationItem: {
        flexDirection: 'row',
        marginBottom: 8,
        paddingLeft: 4,
    },
    recommendationBullet: {
        fontSize: 11,
        color: colors.brand.primary,
        marginRight: 8,
        fontWeight: 'bold',
    },
    recommendationText: {
        fontSize: 10,
        color: colors.gray[700],
        flex: 1,
        lineHeight: 1.4,
    },
    historicalCard: {
        backgroundColor: colors.gray[50],
        padding: 12,
        borderRadius: 6,
        textAlign: 'center',
    },
    historicalText: {
        fontSize: 10,
        color: colors.gray[600],
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        borderTopWidth: 1,
        borderTopColor: colors.gray[200],
        paddingTop: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerText: {
        fontSize: 8,
        color: colors.gray[400],
    },
});

interface RiskFactor {
    source: 'container_issue' | 'document_issue';
    issue_id: string;
    type: string;
    severity: string;
    component: string;
    weight: number;
    title: string;
}

export interface RiskAssessmentData {
    success: boolean;
    container_id: string;
    container_number: string;
    current_phase: string;
    risk_assessment: {
        overall_score: number;
        risk_level: 'minimal' | 'low' | 'medium' | 'high' | 'critical';
        component_scores: {
            document: number;
            timeline: number;
            carrier: number;
            temperature: number;
            customs: number;
        };
        active_issues_count: number;
    };
    risk_factors: RiskFactor[];
    recommendations?: string[];
    historical_comparison?: {
        average_risk: number;
        percentile: number;
        trend?: string;
    };
}

interface RiskAssessmentPDFProps {
    data: RiskAssessmentData;
    containerNumber: string;
}

const componentLabels: Record<string, string> = {
    document: 'Document Risk',
    timeline: 'Timeline Risk',
    carrier: 'Carrier Risk',
    temperature: 'Temperature Risk',
    customs: 'Customs Risk',
};

const getRiskColors = (level: string) => {
    switch (level) {
        case 'minimal':
        case 'low':
            return {
                bg: colors.success[50],
                text: colors.success[700],
                bar: colors.success[500],
            };
        case 'medium':
            return {
                bg: colors.warning[50],
                text: colors.warning[700],
                bar: colors.warning[500],
            };
        case 'high':
        case 'critical':
            return {
                bg: colors.error[50],
                text: colors.error[700],
                bar: colors.error[500],
            };
        default:
            return {
                bg: colors.gray[100],
                text: colors.gray[700],
                bar: colors.gray[500],
            };
    }
};

const getProgressBarColor = (score: number) => {
    if (score <= 0.25) return colors.success[500];
    if (score <= 0.5) return colors.warning[500];
    if (score <= 0.75) return colors.warning[600];
    return colors.error[500];
};

export const RiskAssessmentPDFDocument = ({
    data,
    containerNumber,
}: RiskAssessmentPDFProps) => {
    const riskData = data.risk_assessment;
    const riskLevel = riskData?.risk_level || 'low';
    const riskColors = getRiskColors(riskLevel);
    const generatedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.logo}>EveMind</Text>
                    <Text style={styles.title}>Risk Assessment Report</Text>
                    <Text style={styles.subtitle}>Container: {containerNumber}</Text>
                    <Text style={styles.dateText}>Generated: {generatedDate}</Text>
                </View>

                {/* Overall Score */}
                <View style={[styles.overallScoreCard, { backgroundColor: riskColors.bg }]}>
                    <View style={styles.overallScoreRow}>
                        <View style={styles.overallScoreLeft}>
                            <Text style={styles.overallScoreTitle}>Overall Risk Score</Text>
                            <Text style={styles.overallScoreSubtitle}>
                                {riskData?.active_issues_count || 0} active issue{(riskData?.active_issues_count || 0) !== 1 ? 's' : ''}
                            </Text>
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                            <Text style={styles.overallScoreValue}>
                                {((riskData?.overall_score || 0) * 100).toFixed(0)}%
                            </Text>
                            <View style={[styles.badge, { backgroundColor: riskColors.text }]}>
                                <Text style={[styles.badgeText, { color: '#FFFFFF' }]}>
                                    {riskLevel.toUpperCase()}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* Component Breakdown */}
                {riskData?.component_scores && Object.keys(riskData.component_scores).length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Component Breakdown</Text>
                        {Object.entries(riskData.component_scores).map(([key, score]) => (
                            <View key={key} style={styles.componentRow}>
                                <Text style={styles.componentLabel}>
                                    {componentLabels[key] || key}
                                </Text>
                                <Text style={styles.componentScore}>
                                    {(score * 100).toFixed(0)}%
                                </Text>
                                <View style={styles.progressBarContainer}>
                                    <View
                                        style={[
                                            styles.progressBar,
                                            {
                                                width: `${Math.min(score * 100, 100)}%`,
                                                backgroundColor: getProgressBarColor(score),
                                            },
                                        ]}
                                    />
                                </View>
                            </View>
                        ))}
                    </View>
                )}

                {/* Risk Factors */}
                {data.risk_factors && data.risk_factors.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Active Risk Factors</Text>
                        {data.risk_factors.map((factor, idx) => (
                            <View key={idx} style={styles.factorCard}>
                                <View style={styles.factorHeader}>
                                    <Text style={styles.factorTitle}>{factor.title || factor.type?.replace(/_/g, ' ')}</Text>
                                    <Text style={styles.factorContribution}>
                                        +{((factor.weight || 0) * 100).toFixed(0)}%
                                    </Text>
                                </View>
                                <Text style={styles.factorDescription}>
                                    {factor.type?.replace(/_/g, ' ')} - {factor.severity} severity ({factor.component})
                                </Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Recommendations */}
                {data.recommendations && data.recommendations.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Recommendations</Text>
                        {data.recommendations.map((rec, idx) => (
                            <View key={idx} style={styles.recommendationItem}>
                                <Text style={styles.recommendationBullet}>{'\u2713'}</Text>
                                <Text style={styles.recommendationText}>{rec}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Historical Comparison */}
                {data.historical_comparison && data.historical_comparison.percentile != null && (
                    <View style={styles.historicalCard}>
                        <Text style={styles.historicalText}>
                            This shipment is in the {data.historical_comparison.percentile}th percentile for risk
                            {' '}(average: {((data.historical_comparison.average_risk ?? 0) * 100).toFixed(0)}%)
                        </Text>
                    </View>
                )}

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        EveMind Risk Assessment Report
                    </Text>
                    <Text style={styles.footerText}>
                        Confidential - For internal use only
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

/**
 * Generate and download the Risk Assessment PDF
 */
export async function downloadRiskAssessmentPDF(
    data: RiskAssessmentData,
    containerNumber: string
): Promise<void> {
    const blob = await pdf(
        <RiskAssessmentPDFDocument data={data} containerNumber={containerNumber} />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `risk-assessment-${containerNumber}-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
